/**
 * useSubmitGuard — prevent duplicate POST submits on tablets / slow networks.
 *
 * Returns:
 *   { busy, guard, reset }
 *
 *   busy   — boolean (state). True while a submit is in flight. Bind to the
 *            submit button's `disabled` AND show a spinner / "Saving…" label.
 *   guard  — wrap your async submit:  await guard(async () => { ... });
 *            - Atomic re-entry block via a synchronous useRef latch — TWO
 *              clicks 1ms apart will NOT both pass (React state setBusy is
 *              async, so we can't rely on it for the latch).
 *            - Pushes the idempotency key onto a module-scoped "current key"
 *              stack so the axios interceptor in `lib/api.js` automatically
 *              attaches `Idempotency-Key` to every POST/PUT issued inside the
 *              guard, without the caller having to pass it manually.
 *   reset  — call after the dialog closes / form is cleared to get a fresh
 *            key for the next, semantically-different submission.
 *
 * Feb-2026 fix (user msg: "double click entries still recorded"):
 * - useState(false) was the only latch; React batches state updates so the
 *   second click could enter guard() before busy flipped to true.
 * - Many call sites forgot to pass `{ headers: { 'Idempotency-Key': key } }`,
 *   making the backend cache a no-op for those routes.
 */
import { useRef, useState, useCallback } from "react";
import { setCurrentIdempotencyKey, clearCurrentIdempotencyKey } from "./api";

const newKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function useSubmitGuard() {
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);            // synchronous re-entry latch
  const keyRef = useRef(newKey());

  const guard = useCallback(async (fn) => {
    if (busyRef.current) return undefined;  // hard block re-entry (sync)
    busyRef.current = true;
    setBusy(true);
    setCurrentIdempotencyKey(keyRef.current);
    try {
      return await fn(keyRef.current);
    } finally {
      busyRef.current = false;
      setBusy(false);
      clearCurrentIdempotencyKey(keyRef.current);
    }
  }, []);

  const reset = useCallback(() => {
    keyRef.current = newKey();
  }, []);

  return { busy, guard, reset, idempotencyKey: keyRef.current };
}
